import React, { useEffect, useState } from 'react';
import { useTranslations } from 'use-intl';
import { ExternalLink } from 'react-feather';
import { useSupabaseStore } from './supabaseStore';
import { SUPABASE_BUCKET } from './types';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { GRAY_COLORS } from '@/UIlibrary/globalTheme';

interface DocumentContentProps {
	url: string;
}

const DocumentContent: React.FC<DocumentContentProps> = ({ url }) => {
	const [error, setError] = useState<string | null>(null);
	const [signedUrl, setSignedUrl] = useState<string | null>(null);
	const t = useTranslations('Support');

	const supabase = useSupabaseStore((state) => state.supabase);

	useEffect(() => {
		const getSignedUrl = async () => {
			try {
				const pathMatch = url.match(`/${SUPABASE_BUCKET.WHATSAPP_DOCUMENTS}/(.*)`);
				if (!pathMatch) {
					throw new Error('Invalid storage URL format');
				}

				const path = pathMatch[1];
				const { data, error } = await supabase.storage
					.from(SUPABASE_BUCKET.WHATSAPP_DOCUMENTS)
					.createSignedUrl(path, 3600);

				if (error) throw error;
				setSignedUrl(data.signedUrl);
			} catch (err) {
				console.error('Error getting signed URL:', err);
				setError(t('FailedToGetDocumentAccess'));
			}
		};

		if (url) {
			getSignedUrl();
		}
	}, [url, supabase.storage]);

	const handleDownload = () => {
		if (signedUrl) {
			window.open(signedUrl, '_blank');
		}
	};

	const getFileExtension = (url: string) => {
		const extension = url.split('public').pop()?.split('.').pop()?.toLocaleUpperCase();
		return extension;
	};

	const documentCardStyles = () => {
		switch (getFileExtension(url)) {
			case 'PDF':
				return '#DD2428';
			case 'PPT':
				return '#D35230';
			case 'XLSX':
			case 'XLX':
				return '#1CA25C';
			case 'DOCX':
			case 'DOC':
				return '#0086F9';
			default:
				return GRAY_COLORS.gray600;
		}
	};

	return (
		<div className="relative overflow-hidden">
			{signedUrl && getFileExtension(url) === 'PDF' && (
				<iframe src={signedUrl} className="w-full h-24" />
			)}
			{signedUrl && (
				<div
					className={`flex items-center rounded-lg justify-around gap-8 p-4 bg-slate-50 cursor-pointer hover:bg-slate-100`}
					onClick={handleDownload}
				>
					<div className="flex flex-col items-start gap-2">
						<Typography size="sm" weight="semibold">
							{t('OpenNewWindow')}
						</Typography>
						<div
							className="px-4 rounded-full items-center justify-center"
							style={{ backgroundColor: documentCardStyles() }}
						>
							<Typography size="xs" color="white">
								{getFileExtension(url)}
							</Typography>
						</div>
					</div>
					<ExternalLink size={18} />
				</div>
			)}

			{error && (
				<div className="absolute inset-0 flex items-center justify-center bg-slate-50 rounded-lg p-4">
					<div className="text-red-500 text-sm rounded ">{error}</div>
				</div>
			)}
		</div>
	);
};

export default DocumentContent;
